import { ChartData, ChartDataset } from "chart.js";
import { blue1, blue2, blue3, roofOrange, sunYellow, yellow2 } from '../../colors';

interface ChartColorOptions {
  backgroundColor?: string;
  lineColor?: string;
  tickColor?: string;
  legendColor?: string;
}

interface LineDatasetColorOptions {
  backgroundColor?: string;
  lineAndPointColor?: string;
}

export const lineDatasetOptions = (options: LineDatasetColorOptions = {}, label: string = ''): ChartDataset => ({
  data: [],
  label: label,
  backgroundColor: (options.backgroundColor || yellow2) + '33',
  borderColor: options.lineAndPointColor || sunYellow,
  pointBackgroundColor: options.lineAndPointColor || sunYellow,
  pointBorderColor: '#fff',
  pointHoverBackgroundColor: '#fff',
  pointHoverBorderColor: options.lineAndPointColor || sunYellow,
  fill: 'origin',
});

export const elEnergyChartData = (options: {
  colors?: {
    firstDataset?: LineDatasetColorOptions;
    secondDataset?: LineDatasetColorOptions;
  };
  length?: number;
  labelMapFn?: (v: unknown, k: number) => unknown;
} = {}) => {
  const length = options.length || 12;
  const labelMapFn = options.labelMapFn || ((_, i) => i + 1);

  return {
    datasets: [
      lineDatasetOptions(options.colors?.firstDataset || { backgroundColor: yellow2, lineAndPointColor: sunYellow }),
      lineDatasetOptions(options.colors?.secondDataset || { backgroundColor: blue3, lineAndPointColor: blue1 }),
    ],
    labels: Array.from({ length }, labelMapFn),
  };
};

export const elEnergyChartOptions = (colors: ChartColorOptions = {}) => ({
  responsive: true,
  elements: {
    line: {
      tension: 0.5,
    },
  },
  scales: {
    x: {
      ticks: {
        color: colors.tickColor || sunYellow,
      },
      grid: {
        display: false,
      },
    },
    y: {
      grace: '10%',
      position: 'left',
      ticks: {
        color: colors.tickColor || sunYellow,
      },
    },
  },
  plugins: {
    legend: {
      display: true,
      labels: {
        color: colors.legendColor || blue1,
      },
    },
  },
});

export const moneyChartData = (colors: ChartColorOptions = {}) => ({
  labels: Array.from({ length: 25 }, (_, i) => i + 1),
  responsive: true,
  datasets: [
    {
      data: [],
      label: 'Profit',
      backgroundColor: colors.backgroundColor || sunYellow,
      borderColor: colors.lineColor || roofOrange,
    },
  ],
});

export const moneyChartOptions = (colors: ChartColorOptions = {}) => ({
  layout: {
    padding: {
      top: 10,
    },
  },
  responsive: true,
  scales: {
    x: {
      ticks: {
        color: colors.tickColor || blue1,
      },
      grid: {
        display: false,
      },
    },
    y: {
      ticks: {
        color: colors.tickColor || blue1,
      },
      grace: '5%',
    },
  },
  plugins: {
    legend: {
      display: true,
      labels: {
        color: colors.legendColor || blue1,
      },
    },
  },
});

export const peakPowerChartData: (colors?: {
  firstDataset?: { backgroundColor?: string; borderColor?: string };
  secondDataset?: { backgroundColor?: string; borderColor?: string };
}) => ChartData<'bar'> = (colors: {
  firstDataset?: { backgroundColor?: string; borderColor?: string };
  secondDataset?: { backgroundColor?: string; borderColor?: string };
} = {}) => ({
  labels: Array.from({ length: 12 }, (_, i) => i + 1),
  responsive: true,
  datasets: [
    {
      data: [],
      label: 'With solar',
      backgroundColor: colors.firstDataset?.backgroundColor || sunYellow,
      borderColor: colors.firstDataset?.borderColor || blue1,
    },
    {
      data: [],
      label: 'Without solar',
      backgroundColor: colors.secondDataset?.backgroundColor || blue2,
      borderColor: colors.secondDataset?.borderColor || roofOrange,
    },
  ],
});

export const peakPowerChartOptions = (colors: ChartColorOptions = {}) => ({
  layout: {
    padding: {
      top: 10,
    },
  },
  responsive: true,
  interaction: {
    intersect: false,
  },
  scales: {
    x: {
      stacked: true,
      ticks: {
        color: colors.tickColor || blue1,
      },
      grid: {
        display: false,
      },
    },
    y: {
      ticks: {
        color: colors.tickColor || blue1,
      },
      grace: '5%',
    },
  },
  plugins: {
    legend: {
      reverse: true,
      display: true,
      labels: {
        color: colors.legendColor || blue1,
      },
    },
  },
});
